import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectService } from '../services/project.service';
import { NotificationService } from '../services/notification.service';
import { getStoredUser } from '../helpers/user-storage.util';
import { sanitizeProjectDetails } from '../list/list-grouping.util';
import { computeDashboardKpis, DashboardKpis } from './dashboard-kpi.util';
import { resolveNotificationRoute } from '../notification-dialog/notification-route.util';
import { WorkMapComponent } from './work-map/work-map.component';
import { buildTestId, createTestIdScope } from '../shared/testid/testid.util';

// Recent activity feed shows the latest N items (Spec 10, Task 2).
const RECENT_ACTIVITY_LIMIT = 8;

/**
 * Vendor Dashboard landing page (Spec 10 — Task 1 shell + KPI tiles, Task 2
 * recent activity feed).
 *
 * Greets the vendor and surfaces 4 KPI tiles counted off the same
 * `ProjectService.fetchData` feed the Invitations/Active list pages consume
 * (see dashboard-kpi.util.ts for the counting rules, mirrored from those
 * pages' own status filters). The 4th tile (Receivables due) is gated on
 * Spec 11 — it renders as a disabled placeholder so the 4-tile grid is final.
 *
 * The Recent activity feed below the tiles reuses the same
 * `NotificationService.getVendorNotifications` call the topbar makes (no new
 * backend feed) — that response is already the full read+unread history in
 * one list (topbar itself splits it into read/unread arrays afterward for
 * its own tabs), so this just sorts it newest-first and takes the top
 * `RECENT_ACTIVITY_LIMIT`. Row clicks reuse notification-dialog's exact
 * click→navigate mapping via `resolveNotificationRoute` rather than
 * duplicating a divergent copy of that switch.
 *
 * The work map (Task 3) sits between the tiles and the activity feed as a
 * dedicated `<app-work-map>` child, fed the same sanitized project set.
 */
@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink, WorkMapComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  user = getStoredUser();
  loading = true;

  /** Avatar initials — first + last initial, falling back to the first initial. */
  get userInitials(): string {
    const first = (this.user?.firstName ?? '').trim();
    const last = (this.user?.lastName ?? '').trim();
    const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    return initials || first.charAt(0).toUpperCase() || '?';
  }
  kpis: DashboardKpis = {
    openInvitations: 0,
    upcomingSiteVisits: 0,
    atpsToSign: 0,
  };
  activity: any[] = [];
  // The sanitized project feed, shared with the work map (Task 3) so it plots
  // the vendor's sites off the SAME emission the KPI tiles are counted from —
  // no second fetch.
  projects: any[] = [];

  constructor(
    private projectService: ProjectService,
    private notificationService: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.projectService
      .fetchData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.projects = sanitizeProjectDetails(result);
        this.kpis = computeDashboardKpis(this.projects);
        this.loading = false;
      });

    this.loadActivity();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onActivityClick(item: any): void {
    const resolved = resolveNotificationRoute(item, this.projects);
    if (resolved) {
      this.router.navigate(
        resolved.commands,
        resolved.state ? { state: resolved.state } : {}
      );
    }
  }

  // Per-row identity for testids: the notification's own `id` (a persisted UUID
  // from connect-service) — stable per instance regardless of list order or
  // refresh, so an already-open Playwright selector keeps pointing at the same
  // row. Falls back to createdOn/subject only for an id-less item; without the
  // id, two notifications minted in one server op share a createdOn and collide.
  // Mirrors notification-dialog's `notificationIdentity()`.
  private activityIdentity(item: any): string {
    return String(item?.id ?? item?.createdOn ?? item?.subject ?? 'activity');
  }

  // Recomputed on each access (the activity list is small and only rendered
  // once loaded) rather than cached — keeps this in step with `activity`
  // without separate change-tracking plumbing. `createTestIdScope` dedupes
  // the rare case of two items sharing the same identity.
  get activityTestIds(): string[] {
    const scope = createTestIdScope();
    return this.activity.map((item) =>
      buildTestId(scope, 'dashboard-activity-row', this.activityIdentity(item))
    );
  }

  private loadActivity(): void {
    if (!this.user?.id) {
      return;
    }
    this.notificationService
      .getVendorNotifications(this.user.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        const notifications = Array.isArray(result) ? result : [];
        this.activity = [...notifications]
          .sort(
            (a, b) =>
              new Date(b?.createdOn ?? 0).getTime() -
              new Date(a?.createdOn ?? 0).getTime()
          )
          .slice(0, RECENT_ACTIVITY_LIMIT);
      });
  }
}
